import { useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useClickOutsideBlock } from '../../helpers/useClickOutside';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { LoaderSpinner } from '../LoaderSpinner';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import {
  CustomReportsLoadingKeys,
  getCustomReports,
  saveCustomReport,
} from '../../slices/customReports';

const columnLabel = (column) => column?.label || column?.key || 'Unnamed column';

const moveItem = (items, index, direction) => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const next = [...items];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
};

export const CustomReportColumnsEditModal = ({
  isOpen,
  cancelClick,
  report,
  marginFromSidebar,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const modalRef = useRef(null);
  useClickOutsideBlock(modalRef, cancelClick);
  const { loadingKeys } = useSelector((state) => state.customReports);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [staleColumns, setStaleColumns] = useState([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [error, setError] = useState(null);

  const isSaving = loadingKeys.includes(CustomReportsLoadingKeys.save);
  const availableByKey = useMemo(
    () => new Map(availableColumns.map((column) => [column.key, column])),
    [availableColumns],
  );
  const selectedAvailableColumns = useMemo(
    () => selectedColumns.filter((key) => availableByKey.has(key)),
    [availableByKey, selectedColumns],
  );
  const unselectedColumns = useMemo(
    () => availableColumns.filter((column) => !selectedColumns.includes(column.key)),
    [availableColumns, selectedColumns],
  );

  useEffect(() => {
    if (!isOpen || !report) return;
    setName(report.name || '');
    setDescription(report.description || '');
    setSelectedColumns(Array.isArray(report.columns) ? report.columns : []);
    setStaleColumns([]);
    setAvailableColumns([]);
    setError(null);

    let cancelled = false;
    const loadColumns = async () => {
      setIsLoadingColumns(true);
      try {
        const response = await fetch(`/api/v1/custom-reports/base-reports/${report.baseReportId}/columns`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(json?.error || 'Failed to load report columns');
        if (!cancelled) {
          const columns = Array.isArray(json.columns) ? json.columns : [];
          const availableKeys = new Set(columns.map((column) => column.key));
          const savedColumns = Array.isArray(report.columns) ? report.columns : [];
          setAvailableColumns(columns);
          setSelectedColumns(savedColumns.filter((key) => availableKeys.has(key)));
          setStaleColumns(savedColumns.filter((key) => !availableKeys.has(key)));
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || 'Failed to load report columns');
      } finally {
        if (!cancelled) setIsLoadingColumns(false);
      }
    };
    loadColumns();
    return () => { cancelled = true; };
  }, [isOpen, report]);

  const toggleColumn = (key) => {
    setSelectedColumns((current) =>
      current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key],
    );
  };

  const handleSave = async () => {
    if (!report || selectedAvailableColumns.length === 0) {
      setError('Select at least one available column.');
      return;
    }
    setError(null);
    try {
      const result = await dispatch(saveCustomReport({
        id: report.id,
        name: name.trim(),
        description: description.trim(),
        baseReportId: report.baseReportId,
        columns: selectedAvailableColumns,
        defaultSort: report.defaultSort || null,
      })).unwrap();
      await dispatch(getCustomReports()).unwrap();
      toast.success('Report columns updated', { position: 'bottom-right' });
      onSaved?.(result.report);
      cancelClick?.();
    } catch (saveError) {
      setError(typeof saveError === 'string' ? saveError : 'Failed to update report columns');
    }
  };

  if (!isOpen || !report) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto overflow-x-hidden p-4" aria-hidden="true">
      <div className="fixed inset-0 z-40 bg-gray-700 opacity-30" />
      <div
        ref={modalRef}
        style={{
          '--content-width': marginFromSidebar ? `calc(1380px - ${marginFromSidebar}px)` : '1380px',
          marginLeft: marginFromSidebar ? `${marginFromSidebar}px` : '0',
        }}
        className="relative z-50 max-h-[90vh] w-full max-w-[min(96vw,var(--content-width))] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl md:max-w-2xl"
      >
        {isSaving && <OverlayGrayBackground />}
        {isSaving && (
          <div className="absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2">
            <LoaderSpinner colorSpinner="black" height="96" width="96" border="7" />
          </div>
        )}
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit columns</h2>
            <p className="mt-1 text-sm text-gray-500">Choose and order the columns shown in this report.</p>
          </div>
          <button type="button" onClick={cancelClick} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-5 px-5 py-5">
          {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          {staleColumns.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Removed columns are no longer available and will not be saved: {staleColumns.join(', ')}.
            </div>
          )}
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Report name</span>
            <input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Description</span>
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={2} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
          </label>
          {isLoadingColumns ? (
            <div className="flex items-center justify-center py-8"><LoaderSpinner colorSpinner="black" height="32" width="32" border="3" /></div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Selected columns</p>
              <div className="space-y-1 rounded-md border border-gray-200 p-2">
                {selectedAvailableColumns.map((key, index) => {
                  const column = availableByKey.get(key);
                  return (
                    <div key={key} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
                      <input type="checkbox" checked onChange={() => toggleColumn(key)} className="h-4 w-4 rounded border-gray-300 accent-gray-900" />
                      <span className="min-w-0 flex-1 text-sm text-gray-900">{columnLabel(column)}</span>
                      <span className="text-xs text-gray-500">{column.type || 'string'}</span>
                      <button type="button" disabled={index === 0} onClick={() => setSelectedColumns((current) => moveItem(current, current.indexOf(key), -1))} className="rounded border border-gray-200 px-2 py-0.5 text-xs disabled:opacity-40">↑</button>
                      <button type="button" disabled={index === selectedAvailableColumns.length - 1} onClick={() => setSelectedColumns((current) => moveItem(current, current.indexOf(key), 1))} className="rounded border border-gray-200 px-2 py-0.5 text-xs disabled:opacity-40">↓</button>
                    </div>
                  );
                })}
                {selectedAvailableColumns.length === 0 && <p className="px-2 py-2 text-sm text-gray-500">No columns selected.</p>}
              </div>
              <p className="text-sm font-medium text-gray-700">Available columns</p>
              <div className="space-y-1 rounded-md border border-gray-200 p-2">
                {unselectedColumns.map((column) => (
                  <label key={column.key} className="flex items-center gap-3 rounded px-2 py-1.5 text-sm hover:bg-gray-50">
                    <input type="checkbox" checked={false} onChange={() => toggleColumn(column.key)} className="h-4 w-4 rounded border-gray-300 accent-gray-900" />
                    <span className="flex-1 text-gray-900">{columnLabel(column)}</span>
                    <span className="text-xs text-gray-500">{column.type || 'string'}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={cancelClick} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={isSaving || isLoadingColumns} className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-50">Save changes</button>
          </div>
        </div>
      </div>
    </div>
  );
};
